import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getMemberships, deleteMembership } from '../../actions/membership';
import { setAlert } from '../../actions/alert';
import { Plus, Search, Edit, Trash2, Crown, AlertTriangle } from 'lucide-react';

const Memberships = ({ getMemberships, deleteMembership, setAlert, memberships, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [membershipToDelete, setMembershipToDelete] = useState(null);
    const [filteredMemberships, setFilteredMemberships] = useState([]);

    useEffect(() => {
        document.title = 'Memberships';
        getMemberships();
    }, []);

    useEffect(() => {
        if (memberships) {
            const filtered = memberships.filter(membership =>
                membership.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                membership.info?.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredMemberships(filtered);
        }
    }, [memberships, searchTerm]);

    const handleDeleteClick = (membership) => {
        setMembershipToDelete(membership);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (membershipToDelete) {
            deleteMembership(membershipToDelete._id);
            setShowDeleteModal(false);
            setMembershipToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setMembershipToDelete(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <Crown size={32} className="me-3 text-primary" />
                                    Memberships
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage your membership plans and configurations
                                </p>
                            </div>
                            <Link 
                                to="/inventory/v1.2/memberships/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Create New Membership
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Search and Stats Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search memberships by name or description..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{memberships?.length || 0}</div>
                                        <div className="small">Total Memberships</div>
                                    </div>
                                    <Crown size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Memberships Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading memberships...</p>
                    </div>
                ) : filteredMemberships && filteredMemberships.length > 0 ? (
                    <div className="row">
                        {filteredMemberships.map((membership) => (
                            <div key={membership._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div 
                                    className="card shadow-sm border-0 h-100" 
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    {membership.name}
                                                </h5>
                                                <span className="badge bg-success mb-2" style={{ fontSize: '0.75rem' }}>
                                                    Membership Plan
                                                </span>
                                            </div>
                                            <div className="btn-group">
                                                <Link 
                                                    to={`/inventory/v1.2/memberships/${membership._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Membership"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(membership)}
                                                    title="Delete Membership"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body pt-2">
                                        <div className="row g-3">
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-info">
                                                        {membership.info?.benefits?.length || 0}
                                                    </div>
                                                    <div className="small text-muted">Benefits</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-success">
                                                        {membership.info?.faqs?.length || 0}
                                                    </div>
                                                    <div className="small text-muted">FAQs</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-warning">
                                                        {membership.productPackage ? '✓' : '✗'}
                                                    </div>
                                                    <div className="small text-muted">Package</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-secondary">
                                                        {membership.info?.description ? '✓' : '✗'}
                                                    </div>
                                                    <div className="small text-muted">Description</div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Description Preview */}
                                        {membership.info?.description && (
                                            <div className="mt-3 pt-3 border-top">
                                                <div className="small text-muted mb-2">Description:</div>
                                                <p className="small text-muted mb-0" style={{ 
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {membership.info.description}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer bg-light border-top-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link 
                                                to={`/inventory/v1.2/memberships/${membership._id}`}
                                                className="btn btn-primary btn-sm"
                                            >
                                                <Edit size={14} className="me-1" />
                                                Manage
                                            </Link>
                                            <small className="text-muted">
                                                ID: {membership._id?.slice(-6)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <Crown size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted mb-3">
                            {searchTerm ? 'No memberships found' : 'No memberships created yet'}
                        </h4>
                        <p className="text-muted mb-4">
                            {searchTerm 
                                ? `No memberships match "${searchTerm}". Try adjusting your search.`
                                : 'Get started by creating your first membership plan.'
                            }
                        </p>
                        {!searchTerm && (
                            <Link 
                                to="/inventory/v1.2/memberships/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Create Your First Membership
                            </Link>
                        )}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title d-flex align-items-center text-danger">
                                        <AlertTriangle size={24} className="me-2" />
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={cancelDelete}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <Trash2 size={48} className="text-danger" />
                                        </div>
                                        <h6 className="mb-3">Delete Membership "{membershipToDelete?.name}"?</h6>
                                        <p className="text-muted mb-4">
                                            This action cannot be undone. All associated data including benefits, 
                                            FAQs, and configurations will be permanently deleted.
                                        </p>
                                        <div className="alert alert-warning d-flex align-items-center">
                                            <AlertTriangle size={20} className="me-2" />
                                            <small>
                                                <strong>Warning:</strong> This will permanently delete the membership and all its data.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={cancelDelete}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        <Trash2 size={16} className="me-2" />
                                        Delete Membership
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

Memberships.propTypes = {
    getMemberships: PropTypes.func.isRequired,
    deleteMembership: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    memberships: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
    memberships: state.membership.memberships,
    loading: state.membership.loading
});

export default connect(mapStateToProps, { getMemberships, deleteMembership, setAlert })(Memberships); 